import React, { useState, useContext, useEffect, useRef } from "react";
import { UserContext } from "../../context/userContext/userContext";
import { useStyle } from "../../context/styleContext/styleContext";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  Grid,
  Paper,
  Stack,
  Button,
  Divider,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Seo from "../../components/Seo";
import { Link as RouterLink } from "react-router-dom";

export default function Support() {
  const { user } = useContext(UserContext);
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const isMobile = useMediaQuery("(max-width:600px)");
  const topContainer = useRef();

  useEffect(() => {
    topContainer.current?.scrollIntoView({ block: "start" });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const supportSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Support | Parish Audio",
    url: "https://parishaudio.com/support",
    description:
      "Get support for Parish Audio plugins, including downloads, installation, activation, subscriptions, and troubleshooting.",
  };

  const quickCards = [
    {
      title: "Download your plugins",
      text: "After purchase or subscription, your downloads are available from My Gear and the product pages.",
      icon: <DownloadRoundedIcon sx={{ color: style.accentGreen }} />,
    },
    {
      title: "Sign in to unlock",
      text: "Open the plugin in your DAW and enter your Parish Audio username and password to unlock it.",
      icon: <LockOpenRoundedIcon sx={{ color: style.accentGreen }} />,
    },
    {
      title: "Need help?",
      text: "If something is not working the way it should, contact support and we will help you get up and running.",
      icon: <HelpOutlineRoundedIcon sx={{ color: style.accentGreen }} />,
    },
  ];

  const accordionSx = {
    boxShadow: 8,
    backgroundColor: style.color1,
    color: style.color4,
    mb: 2,
    borderRadius: 2,
    overflow: "hidden",
    "&:before": { display: "none" },
  };

  const summarySx = {
    fontSize: "1.05rem",
    fontWeight: "bold",
    backgroundColor: style.color1,
    color: style.color4,
    "& .MuiAccordionSummary-content": {
      margin: 0,
    },
  };

  const detailsSx = {
    backgroundColor: style.darkerPanel,
    color: style.color3,
    fontSize: "1rem",
    lineHeight: 1.75,
  };

  return (
    <>
      <Seo
        title="Support | Parish Audio"
        description="Get support for Parish Audio plugins, including downloads, installation, activation, subscriptions, and troubleshooting."
        canonical="https://parishaudio.com/support"
        schema={supportSchema}
      />

      <Box sx={{ width: "95%", maxWidth: 1100, mx: "auto", minHeight: height - 160, py: 4 }}>
        <Stack spacing={3}>
          <Paper
            sx={{
              p: { xs: 2.5, md: 4 },
              borderRadius: 3,
              backgroundColor: style.color1,
              color: style.color4,
              boxShadow: 10,
            }}
          >
            <Stack spacing={2.5}>
              <Box ref={topContainer}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2rem", md: "2.8rem" },
                    fontWeight: 800,
                    mb: 1,
                    color: style.color4,
                  }}
                >
                  Support
                </Typography>

                <Typography
                  sx={{
                    color: style.color3,
                    lineHeight: 1.85,
                    maxWidth: 820,
                  }}
                >
                  We want Parish Audio products to be simple to install, easy to unlock,
                  and enjoyable to use. If you run into any trouble, we are here to help.
                </Typography>
              </Box>
              <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: "1.2rem", md: "1.2rem" },
                    fontWeight: 800,
                    mb: 1,
                    color: style.color4,
                  }}
                >
                  Plugin Installation:
                </Typography>

              <Grid container spacing={2}>
                {quickCards.map((card) => (
                  <Grid item xs={12} md={4} key={card.title} sx={{ display: "flex" }}>
                    <Paper
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        backgroundColor: style.darkPanel,
                        boxShadow: 4,
                        width: "100%",
                      }}
                    >
                      <Stack spacing={1}>
                        {card.icon}
                        <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                          {card.title}
                        </Typography>
                        <Typography sx={{ color: style.color3, lineHeight: 1.7, fontSize: "0.95rem" }}>
                          {card.text}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <Button
                  component={RouterLink}
                  to="/my-gear"
                  variant="contained"
                  sx={{
                    backgroundColor: style.color2,
                    color: style.color4,
                    fontWeight: 700,
                    px: 2.5,
                    py: 1.1,
                    "&:hover": {
                      backgroundColor: style.color2,
                      filter: "brightness(1.08)",
                    },
                  }}
                >
                  Go to My Gear
                </Button>

                <Button
                  component={RouterLink}
                  to="/faq"
                  variant="outlined"
                  sx={{
                    borderColor: style.color2,
                    color: style.color4,
                    fontWeight: 700,
                    px: 2.5,
                    py: 1.1,
                    "&:hover": {
                      borderColor: style.color4,
                      backgroundColor: "rgba(255,255,255,0.04)",
                    },
                  }}
                >
                  View FAQ
                </Button>

                <Button
                  component="a"
                  href="mailto:support@parishaudio.com"
                  variant="text"
                  startIcon={<EmailRoundedIcon />}
                  sx={{
                    color: style.color4,
                    fontWeight: 700,
                    px: 0.5,
                    "&:hover": {
                      backgroundColor: "transparent",
                      color: style.color4,
                    },
                  }}
                >
                  support@parishaudio.com
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              backgroundColor: style.color1,
              color: style.color4,
              boxShadow: 10,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: "1.35rem",
                fontWeight: 800,
                mb: 2,
                color: style.color4,
              }}
            >
              Help Topics
            </Typography>

            <Accordion sx={accordionSx} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: style.color2 }} />} sx={summarySx}>
                How do I install my plugin?
              </AccordionSummary>
              <AccordionDetails sx={detailsSx}>
                <Typography sx={{ color: style.color4, fontWeight: 700, mb: 1 }}>
                  We recommend closing your DAW before running the installer.
                </Typography>

                <Box
                  component="ul"
                  sx={{
                    pl: 2.5,
                    m: 0,
                    "& li": { mb: 1.25 },
                  }}
                >
                  <li>
                    Download the installer from the{" "}
                    <RouterLink to="/my-gear" style={{ color: style.color2 }}>
                      My Gear
                    </RouterLink>{" "}
                    page or from the product page.
                  </li>
                  <li>Run the installer. It will place the plugin in the standard VST3 location for your system.</li>
                  <li>Open your DAW and load the plugin.</li>
                  <li>When the plugin opens for the first time, sign in with your Parish Audio account to unlock it.</li>
                </Box>
              </AccordionDetails>
            </Accordion>


            <Accordion sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: style.color2 }} />} sx={summarySx}>
                Can I use the plugin offline?
              </AccordionSummary>
              <AccordionDetails sx={detailsSx}>
                <Box
                  component="ul"
                  sx={{
                    pl: 2.5,
                    m: 0,
                    "& li": { mb: 1.25 },
                  }}
                >
                  <li>An internet connection is needed the first time you sign in to unlock the plugin.</li>
                  <li>After that, owned plugins can continue working offline on that computer.</li>
                  <li>Subscription plugins may need to reconnect periodically so the license can refresh automatically.</li>
                  <li>If you log out you will need an internet connection to sign back in.</li>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: style.color2 }} />} sx={summarySx}>
                Where are my VST3 files installed?
              </AccordionSummary>
              <AccordionDetails sx={detailsSx}>
                <Stack spacing={2}>
                  <Paper
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.035)",
                      boxShadow: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <FolderOpenRoundedIcon sx={{ color: style.color2, mt: "2px" }} />
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                          Windows
                        </Typography>
                        <Typography sx={{ color: style.color3 }}>
                          C:\Program Files\Common Files\VST3
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>

                  <Paper
                    sx={{
                      p: 1.75,
                      borderRadius: 2,
                      backgroundColor: "rgba(255,255,255,0.035)",
                      boxShadow: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <FolderOpenRoundedIcon sx={{ color: style.color2, mt: "2px" }} />
                      <Box>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.5 }}>
                          Mac
                        </Typography>
                        <Typography sx={{ color: style.color3 }}>
                          /Library/Audio/Plug-Ins/VST3
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: style.color2 }} />} sx={summarySx}>
                Where can I download my plugins again later?
              </AccordionSummary>
              <AccordionDetails sx={detailsSx}>
                You can download your plugins anytime from your{" "}
                <RouterLink to="/my-gear" style={{ color: style.color2 }}>
                  My Gear
                </RouterLink>{" "}
                page. Download links may also be available from the individual product pages.
              </AccordionDetails>
            </Accordion>

            <Accordion sx={accordionSx}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: style.color2 }} />} sx={summarySx}>
                How do I contact support?
              </AccordionSummary>
              <AccordionDetails sx={detailsSx}>
                <Stack spacing={1.5}>
                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    If you need help with downloads, installation, unlocking, subscriptions,
                    or anything else, please email us and we will be glad to help.
                  </Typography>

                  <Button
                    component="a"
                    href="mailto:support@parishaudio.com"
                    startIcon={<EmailRoundedIcon />}
                    variant="contained"
                    sx={{
                      alignSelf: "flex-start",
                      backgroundColor: style.color2,
                      color: style.color4,
                      fontWeight: 700,
                      px: 2.5,
                      py: 1.1,
                      "&:hover": {
                        backgroundColor: style.color2,
                        filter: "brightness(1.08)",
                      },
                    }}
                  >
                    Email support@parishaudio.com
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Paper>

          <Paper
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 3,
              backgroundColor: style.color1,
              color: style.color4,
              boxShadow: 8,
            }}
          >
            <Stack spacing={1.5}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 800,
                  color: style.color4,
                }}
              >
                We are here to help
              </Typography>

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <CheckCircleRoundedIcon sx={{ color: style.accentGreen, mt: "2px", fontSize: "1.1rem" }} />
                <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                  If something is not working, contact support before assuming the plugin is broken.
                  Many issues can be resolved quickly with the right setup steps.
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <CheckCircleRoundedIcon sx={{ color: style.accentGreen, mt: "2px", fontSize: "1.1rem" }} />
                <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                  We want you to feel confident purchasing and using Parish Audio products,
                  and we are happy to help if you need assistance.
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>
    </>
  );
}
// import {React, useState, useContext, useEffect, useRef} from "react"
// import{UserContext} from '../../context/userContext/userContext';
// import { useStyle } from '../../context/styleContext/styleContext';
// import ReactGA from 'react-ga4';
// import {
//   Box,
//   Typography,
//   Accordion,
//   AccordionSummary,
//   AccordionDetails,
//   useMediaQuery,
// } from "@mui/material";
// import { ExpandMore } from "@mui/icons-material";
// import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
// import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
// import Seo from "../../components/Seo";


// export default function Support(){

//   const { user } = useContext(UserContext);
//   const { isLoading } = useContext(UserContext);
//   const { style, setStyle } = useStyle();

//   const [height, setHeight] = useState(window.innerHeight)
//   const [width, setWidth] = useState(window.setWidth)

//   useEffect(() => {
//     function handleResize() {      
//       setWidth(window.innerWidth)
//       setHeight(window.innerHeight)
//   }
  
//     window.addEventListener('resize', handleResize)
//   })

//   const isMobile = useMediaQuery("(max-width:600px)");
//   const topContainer = useRef();
//     useEffect(() => {
//         // To make sure page starts from the top
//         topContainer.current.scrollIntoView({ block: "end" });
//     }, []);

//   const changeBackground = () => {
//     setStyle({ ...style, backgroundColor: 'rgb(0,255,0)' });
//   };

//     return(
//         <>

//     <Seo
//       title="Support | Parish Audio"
//       description="Get support for Parish Audio plugins, including downloads, installation, activation, subscriptions, and troubleshooting."
//       canonical="https://parishaudio.com/support"
//     />

// <div style={{width:'95%', margin:'auto', minHeight:height-160}}>
//     <Box
//       sx={{
//         maxWidth: "900px",
//         margin: "auto",
//         pl: isMobile ? 2 : 4,
//         pr: isMobile ? 2 : 4,
//         pt: isMobile ? 1 : 2,
//         pb: isMobile ? 2 : 4,
//         mt: 4,
//         mb: 4,
//         backgroundColor: style.color1,
//         borderRadius: 2,
//         color: style.color4,
//         marginBottom:'25px'
//       }}
//     >
//       <h1 ref={topContainer} className='h1-A' style={{marginTop:'0px', marginBottom:'20px', fontSize:'1.5em'}}>
//         Support
//       </h1>


//       <Accordion
        
//         sx={{
//           boxShadow:10,
//           backgroundColor: style.color1,
//           color: style.color1,
//           mb: 2,
//           borderRadius: 1,
//           "&:before": { display: "none" },
//         }}
//         >
//         <AccordionSummary
//           expandIcon={<ExpandMore sx={{ color: style.color2 }} />}
//           sx={{
//             fontSize: "1.2rem",
//             fontWeight: "bold",
//             backgroundColor: style.color1,
//             filter:'brightness(1.2)',
//             color:style.color4,
//             borderRadius: 1,
//             "& .MuiAccordionSummary-content": {
//               margin: 0,
//             },
//           }}
//         >
//             How do I install my plugin?
//           </AccordionSummary>
//           <AccordionDetails
//             sx={{ backgroundColor: style.color1, filter:'brightness(1.35)',fontSize: "1.1rem", color:style.color4, textAlign:'center' }}
//           >
//             <strong style={{fontSize:'1.15rem', filter:'brightness(0.75)', textAlign:'center', margin:'auto', width:'100%'}}>We recommend closing your DAW or host program prior to installation.</strong>

//             <ul className='a' style={{fontSize: "1.1rem", filter:'brightness(0.75)', marginTop:'25px', textAlign:'left', marginLeft:'10px'}}>

//               <li style={{marginBottom:'20px'}}>
//                 <strong>Step 1:</strong> Download the app installer from the <a href="/my-gear"><strong>My Gear</strong></a> page. 
//               </li>
//               <li style={{marginBottom:'20px'}}>
//                 <strong>Step 2:</strong> Run the app installer. This will install your plugin to the correct location so it can be recognized automatically by your DAW. 
//               </li>
//               <li style={{marginBottom:'20px'}}>
//                 <strong>Step 3:</strong> When you open the plugin for the first time, enter your email and password to unlock it.
//               </li>
//               <li style={{marginBottom:'20px'}}>
//                 <strong>Step 4:</strong> Enjoy!
//               </li>
//             </ul>

//           </AccordionDetails>
//         </Accordion>


//         <Accordion
        
//         sx={{
//           boxShadow:10,
//           backgroundColor: style.color1,
//           color: style.color1,
//           mb: 2,
//           borderRadius: 1,
//           "&:before": { display: "none" },
//         }}
//         >
//         <AccordionSummary
//           expandIcon={<ExpandMore sx={{ color: style.color2 }} />}
//           sx={{
//             fontSize: "1.2rem",
//             fontWeight: "bold",
//             backgroundColor: style.color1,
//             filter:'brightness(1.2)',
//             color:style.color4,
//             borderRadius: 1,
//             "& .MuiAccordionSummary-content": {
//               margin: 0,
//             },
//           }}
//         >
//             Where are my VST3 files located on my device?
//           </AccordionSummary>
//           <AccordionDetails
//             sx={{ backgroundColor: style.color1, filter:'brightness(1.35)',fontSize: "1.0rem", color:style.color4, textAlign:'left' }}
//           >
//                     On Windows devices, your VST3 plugins are installed to the following folder:
//                     <br />
                    
//                      <strong>C:\Program Files\Common Files\VST3</strong>
//                      <br />
//                      <br />

//                       On Mac computers, your VST3 plugins are installed to the following folder:
//                       <br />

//                       <strong>/Library/Audio/Plug-Ins/VST3</strong>    

//           </AccordionDetails>
//         </Accordion>


//         <Accordion
        
//         sx={{
//           boxShadow:10,
//           backgroundColor: style.color1,
//           color: style.color1,
//           mb: 2,
//           borderRadius: 1,
//           "&:before": { display: "none" },
//         }}
//         >
//         <AccordionSummary
//           expandIcon={<ExpandMore sx={{ color: style.color2 }} />}
//           sx={{
//             fontSize: "1.2rem",
//             fontWeight: "bold",
//             backgroundColor: style.color1,
//             filter:'brightness(1.2)',
//             color:style.color4,
//             borderRadius: 1,
//             "& .MuiAccordionSummary-content": {
//               margin: 0,
//             },
//           }}
//         >
//             Contact Us
//           </AccordionSummary>
//           <AccordionDetails
//             sx={{ backgroundColor: style.color1, filter:'brightness(1.35)',fontSize: "1.1rem", color:style.color4, textAlign:'left' }}
//           >
//               We would love to hear from you. If you have questions or need assistance, please contact us at <a href="mailto:support@parishaudio.com" style={{color:style.color2, fontSize:'14pt'}}><strong>support@parishaudio.com</strong></a>.
//           </AccordionDetails>
//         </Accordion>

//     </Box>
//     </div>

//         </>

//     )
// }